import React, { forwardRef } from 'react';
import { useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import DSBox from '../../../design-system/DSBox';
import DSTypography from '../../../design-system/Typography/DSTypography';
import DSTooltip from '../../../design-system/DSTooltip';
import { ReactComponent as PlusIcon } from '../../../design-system/Icons/PlusIcon.svg';
import { CalendarEvent } from '../../../api/calendarEvent';
import {
  KLYDO_YELLOW,
  KLYDO_BLACK,
  KLYDO_WHITE,
  NATIONAL_HOLIDAY_BACKGROUND_COLOR,
  NATIONAL_HOLIDAY_HOVER_BACKGROUND_COLOR,
  CALENDAR_EVENT_HOVER_BACKGROUND_COLOR,
} from '../../../consts/colors';
import { generateObjectId } from '../../../utils';
import {
  isCalendarEventPast,
  isNationalHoliday,
} from '../../../utils/calendarEvents';

type CalendarEventListItemProps = {
  calendarEvent: CalendarEvent;
};

const CalendarEventListItem = forwardRef<
  HTMLDivElement,
  CalendarEventListItemProps
>(({ calendarEvent }, ref) => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();

  const calendarEventDate = new Date(
    calendarEvent.date.year,
    calendarEvent.date.month - 1,
    calendarEvent.date.day,
  );
  const day = dayjs(calendarEventDate).format('DD');
  const month = dayjs(calendarEventDate).format('MMM').toUpperCase();
  const dayOfWeek = dayjs(calendarEventDate).format('dddd');

  const isHighlighted = isNationalHoliday(calendarEvent);

  const chipBackgroundColor = isHighlighted
    ? KLYDO_YELLOW
    : CALENDAR_EVENT_HOVER_BACKGROUND_COLOR;
  const chipTextColor = isHighlighted ? KLYDO_BLACK : KLYDO_WHITE;

  const containerBackground = isHighlighted
    ? NATIONAL_HOLIDAY_BACKGROUND_COLOR
    : palette.paper.elevation2;
  const hoverContainerBackground = isHighlighted
    ? NATIONAL_HOLIDAY_HOVER_BACKGROUND_COLOR
    : palette.paper.elevation10;

  const handleClick = () => {
    if (!isDatePast) {
      const createdObjectId = generateObjectId();
      navigate(`/klydo/${createdObjectId}`, {
        state: {
          calendarEventId: calendarEvent.id,
        },
      });
    }
  };

  const isDatePast = isCalendarEventPast(calendarEvent);

  return (
    <DSBox
      ref={ref}
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        background: containerBackground,
        borderRadius: '50px',
        gap: spacing(4),
        minHeight: '48px',
        padding: spacing(0.5, 3, 0.5, 0.5),
        cursor: isDatePast ? 'default' : 'pointer',
        opacity: isDatePast ? 0.6 : 1,
        transition: 'opacity 0.2s ease',
        '&:hover': {
          background: isDatePast
            ? containerBackground
            : hoverContainerBackground,
          '& svg': {
            color: isDatePast ? palette.text.disabled : KLYDO_WHITE,
          },
        },
      }}
    >
      <DSBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: chipBackgroundColor,
          height: spacing(4),
          width: spacing(12.5),
          borderRadius: '58px',
        }}
      >
        <DSTypography
          variant="body2"
          sx={{
            color: chipTextColor,
          }}
        >
          {month} {day}
        </DSTypography>
      </DSBox>

      <DSTypography
        variant="body1"
        sx={{
          color: isDatePast ? palette.text.disabled : KLYDO_WHITE,
          width: { xs: spacing(7.5), sm: spacing(10), md: spacing(18.75) },
        }}
      >
        {dayOfWeek}
      </DSTypography>

      <DSTypography
        variant="value"
        sx={{
          color: isDatePast ? palette.text.disabled : KLYDO_WHITE,
          flex: 1,
        }}
      >
        {calendarEvent.name}
      </DSTypography>

      <DSTooltip
        title={
          isDatePast ? 'This event has passed' : 'Add klydo to this unique feed'
        }
      >
        <DSBox
          sx={{
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isDatePast ? palette.text.disabled : palette.action.active,
          }}
        >
          <PlusIcon width={20} height={20} />
        </DSBox>
      </DSTooltip>
    </DSBox>
  );
});

CalendarEventListItem.displayName = 'CalendarEventListItem';

export default CalendarEventListItem;
